console.log("Summer (commit " + __COMMIT_HASH__ + ")")

import { createApp } from "vue"
import { useRouter, useRoute } from "vue-router"
import * as Sentry from "@sentry/browser"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import relativeTime from "dayjs/plugin/relativeTime"
import duration from "dayjs/plugin/duration"
import "dayjs/locale/sl"

import { msalInstance } from "./authConfig"

import "bootstrap/dist/css/bootstrap.min.css"
import "bootstrap"
import "../css/app.css"

// Backwards compatibility: make vue-router helpers available as globals
// for components that haven't been migrated yet.
window.VueRouter = { useRouter, useRoute }

import {
  TIME_FORMAT_DISPLAY,
  DATE_FORMAT_DISPLAY,
  DATETIME_FORMAT,
  DATETIME_FORMAT_DISPLAY,
  EMPTY_GUID,
} from "./utils/constants"
import {
  fmtSec,
  fmtMin,
  toCultureIndependentString,
  parseNumberFromString,
  formatTime,
  formatDuration,
  formatRoundUpDuration,
  createQuickDates,
} from "./utils/conversions"
import { toggleMultiSelectFilter } from "./utils/filters"
import router from "./router/router"

/* Global imports */
import OrderBadge from "./components/OrderBadge.vue"
import OrderIsBadge from "./components/OrderIsBadge.vue"
import SelectOrderType from "./components/SelectOrderType.vue"
import SelectStatus from "./components/SelectStatus.vue"
import Attachments from "./components/Attachments.vue"
import Upload from "./components/Upload.vue"
import App from "./App.vue"
import Modal from "./components/Modal.vue"
import SelectEntity from "./components/SelectEntity.vue"
import DisplayEntity from "./components/DisplayEntity.vue"
import Autocomplete from "./components/Autocomplete.vue"
import WorkOrderTimes from "./components/WorkOrderTimes.vue"
import WorkOrderMaterials from "./components/WorkOrderMaterials.vue"
import WorkOrderTransports from "./components/WorkOrderTransports.vue"
import SelectCustomerEntity from "./components/SelectCustomerEntity.vue"
import IntegerRadioGroup from "./components/IntegerRadioGroup.vue"
import BoolFilter from "./components/BoolFilter.vue"
import ChangeHistory from "./components/ChangeHistory.vue"
import DateTimeInput from "./components/DatetimeInput.vue"
import DateTimeFilterSet from "./components/DatetimeFilterSet.vue"
import SelectEnum from "./components/SelectEnum.vue"
import MaterialWarehouses from "./components/MaterialWarehouses.vue"
import { hasRole, hasRoleStartsWith, hasRoleEndsWith } from "./utils/roles"
import DevicesCard from "./components/DevicesCard.vue"
import Paging from "./components/Paging.vue"
import RequiredWarning from "./components/RequiredWarning.vue"

// Extend dayjs with plugins
dayjs.extend(utc)
dayjs.extend(relativeTime)
dayjs.extend(duration)
dayjs.locale("sl")

// Set globals for backwards compatibility with existing code
// (templates reference these via $window.X, and $window = window)
window.dayjs = dayjs
window.CONTEXT = CONTEXT
window.GOOGLE_API_KEY = GOOGLE_API_KEY
window.AZURE_URI = AZURE_URI
window.REDIRECT_URI = REDIRECT_URI

if (CONTEXT === "production" && !!SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: CONTEXT,
    tracesSampleRate: 1.0,
    release: "summer-front@1.0.0",
  })
} else {
  console.log(`Sentry disabled (CONTEXT=${CONTEXT}).`)
}

const hasStatus = (workOrder, statuses) => {
  if (!Array.isArray(statuses)) {
    console.error("statuses should be an array hasStatus")
    return false
  }
  return statuses.some((s) => workOrder?.statusText?.startsWith(s))
}

const app = createApp(App)

app.component("OrderBadge", OrderBadge)
app.component("OrderIsBadge", OrderIsBadge)
app.component("SelectOrderType", SelectOrderType)
app.component("SelectStatus", SelectStatus)
app.component("Paging", Paging)
app.component("Attachments", Attachments)
app.component("Upload", Upload)
app.component("Autocomplete", Autocomplete)
app.component("Modal", Modal)
app.component("SelectEntity", SelectEntity)
app.component("DisplayEntity", DisplayEntity)
app.component("WorkOrderTimes", WorkOrderTimes)
app.component("WorkOrderTransports", WorkOrderTransports)
app.component("SelectCustomerEntity", SelectCustomerEntity)
app.component("RequiredWarning", RequiredWarning)
app.component("WorkOrderMaterials", WorkOrderMaterials)
app.component("ChangeHistory", ChangeHistory)
app.component("DateTimeInput", DateTimeInput)
app.component("DateTimeFilterSet", DateTimeFilterSet)
app.component("SelectEnum", SelectEnum)
app.component("MaterialWarehouses", MaterialWarehouses)
app.component("IntegerRadioGroup", IntegerRadioGroup)
app.component("BoolFilter", BoolFilter)
app.component("DevicesCard", DevicesCard)

app.config.globalProperties.$filters = {
  encode128(value) {
    const _encoder = new Code128Generator()
    return _encoder.encode(value)
  },
}

app.config.globalProperties.$hasRole = hasRole
app.config.globalProperties.$hasRoleStartsWith = hasRoleStartsWith
app.config.globalProperties.$hasRoleEndsWith = hasRoleEndsWith
app.config.globalProperties.$hasStatus = hasStatus
app.config.globalProperties.$fmtSec = fmtSec
app.config.globalProperties.$fmtMin = fmtMin
app.config.globalProperties.$toCultureIndependentString = toCultureIndependentString
app.config.globalProperties.$parseNumberFromString = parseNumberFromString
app.config.globalProperties.$formatTime = formatTime
app.config.globalProperties.$formatDuration = formatDuration
app.config.globalProperties.$formatRoundUpDuration = formatRoundUpDuration
app.config.globalProperties.$createQuickDates = createQuickDates

app.config.globalProperties.$window = window
app.config.globalProperties.$dayjs = dayjs
app.config.globalProperties.$toggleMultiSelect = (obj, key, value) => {
  obj[key] = toggleMultiSelectFilter(obj[key], value)
}

app.config.globalProperties.$log = console.log
app.config.globalProperties.$DATETIME_FORMAT = DATETIME_FORMAT
app.config.globalProperties.$EMPTY_GUID = EMPTY_GUID
app.config.globalProperties.$AZURE_URI = AZURE_URI
app.config.globalProperties.$DATETIME_FORMAT_DISPLAY = DATETIME_FORMAT_DISPLAY
app.config.globalProperties.$DATE_FORMAT_DISPLAY = DATE_FORMAT_DISPLAY
app.config.globalProperties.$TIME_FORMAT_DISPLAY = TIME_FORMAT_DISPLAY

async function initializeApp() {
  try {
    await msalInstance.initialize()
    app.use(router)
    await router.isReady()
    app.mount("#app")
  } catch (error) {
    alert("Error during app initialization: " + error)
  }
}

initializeApp()
